import { useRef, useState, useEffect } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import jsPDF from 'jspdf';
import './AgreementPreview.css';
import sellerSignature from '../assets/seller-signature.jpg';
import { supabase } from '../supabaseClient';
import logoBase64 from '../assets/logo.jpg';
import { useToast } from './Toast';

function AgreementPreview({ formData, agreementType, showGuarantor, subtotal, vat, total, deliveryAmount, remainingBalance, monthlyInstallment, paymentSchedule, extraPaymentTerms, extraStandardTerms, currency, currentUser, onLogActivity, onBack }) {
  const cur = currency || 'AED';
  const buyerSigRef = useRef([]);
  const guarantorSigRef = useRef(null);
  const isGenerating = useRef(false);
  const [cameraActive, setCameraActive] = useState({});
  const [buyerPhotos, setBuyerPhotos] = useState({});
  const [shareLink, setShareLink] = useState('');
  const [shareLoading, setShareLoading] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareModalStep, setShareModalStep] = useState('select'); // 'select' | 'link'
  const [selectedShareBuyer, setSelectedShareBuyer] = useState(null);
  const [generatedLinks, setGeneratedLinks] = useState({});
  const [remoteSignatures, setRemoteSignatures] = useState({}); // { buyerIndex: dataUrl }
  const [remotePhotos, setRemotePhotos] = useState({});         // { buyerIndex: dataUrl }
  const [sigStatuses, setSigStatuses] = useState({});           // { buyerIndex: 'pending'|'signed' }
  const { showToast, ToastComponent } = useToast();
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [printOptions, setPrintOptions] = useState({
    hideHeader: false,
    hideFooter: false,
    office: false,
    customer: false,
    original: true,
    duplicate: false,
  });
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewBuilding, setPreviewBuilding] = useState(false);
  const previewDocRef = useRef(null); // holds { pdf, filename } for the previewed doc
  const previewUrlRef = useRef(null); // holds the object URL so we can revoke it

  // ── Fetch remote signatures on mount & subscribe for real-time updates ──
  useEffect(() => {
    if (!formData.refNumber) return;

    const loadRemoteSignatures = async () => {
      const { data: rows } = await supabase
        .from('agreement_signatures')
        .select('signatures, photos, buyer_index, status')
        .eq('agreement_data->>refNumber', formData.refNumber);

      if (!rows) return;
      const mergedSigs = {};
      const mergedPhotos = {};
      const statuses = {};
      rows.forEach(row => {
        if (row.signatures) Object.assign(mergedSigs, row.signatures);
        if (row.photos) Object.assign(mergedPhotos, row.photos);
        if (row.buyer_index !== null && row.buyer_index !== undefined) {
          statuses[row.buyer_index] = row.status;
        }
      });
      setRemoteSignatures(mergedSigs);
      setRemotePhotos(mergedPhotos);
      setSigStatuses(statuses);
    };

    loadRemoteSignatures();

    // Real-time subscription
    const channel = supabase
      .channel(`remote-sigs-${formData.refNumber}`)
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'agreement_signatures',
      }, (payload) => {
        const row = payload.new;
        // Only handle rows belonging to this agreement
        if (row.agreement_data?.refNumber !== formData.refNumber) return;
        setRemoteSignatures(prev => ({ ...prev, ...(row.signatures || {}) }));
        setRemotePhotos(prev => ({ ...prev, ...(row.photos || {}) }));
        if (row.buyer_index !== null && row.buyer_index !== undefined) {
          setSigStatuses(prev => ({ ...prev, [row.buyer_index]: row.status }));
        }
        showToast(`✍️ Signature received from ${row.agreement_data?.buyers?.[row.buyer_index]?.representativeName || 'a buyer'}!`, 'success');
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [formData.refNumber]); // eslint-disable-line react-hooks/exhaustive-deps

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' }).toUpperCase();
  };

  const terms = [
    "The buyer is bound to examine the goods on arrival and in case of any discrepancy / claim regarding quality / grade of products / machinery, should be informed in writing to seller within TWO (2) working days. The failure of the buyer to comply with this rule, should be considered acceptance of product / machinery in good condition / without any fault or claim.",
    "In case of delay or failure of payment, warranty of products should be deemed as cancelled until / unless pending payments should be cleared in favour of seller.",
    "Payment of the agreement shall be made within Credit period agreed between us as per credit facility terms.",
    "The goods sold under this agreement will remain the property of M/S Almushtaraka Trading Company only. Unless and until the full payment of such goods has been settled to Almushtaraka Trading Company.",
    "If buyer fails to pay the full amount in agreed period, seller has reserve the right to take back his goods, mentioned in this sales agreement.",
    "In case of sale or transfer of business entity / ownership of goods or machinery supplied under this agreement, the BUYER is bound to inform the SELLER and settle his outstanding dues with seller."
  ];

  const openShareModal = () => {
    setShareModalStep('select');
    setSelectedShareBuyer(null);
    setShareLink('');
    setShowShareModal(true);
  };

  const generateShareLink = async (buyerIndex) => {
    setShareLoading(true);
    const buyerName = formData.buyers[buyerIndex]?.representativeName || `Buyer ${buyerIndex + 1}`;
    const token = `${formData.refNumber.replace(/\//g, '-')}-b${buyerIndex}-${Date.now()}`;
    const { error } = await supabase.from('agreement_signatures').insert([{
      token,
      agreement_data: { ...formData, total, remainingBalance },
      agreement_type: agreementType,
      status: 'pending',
      buyer_index: buyerIndex,
    }]);

    if (error) {
      showToast('Failed to generate share link. Please try again.', 'error');
      setShareLoading(false);
      return;
    }

    const link = `${window.location.origin}/sign/${token}`;
    setShareLink(link);
    setGeneratedLinks(prev => ({ ...prev, [buyerIndex]: link }));
    setShareModalStep('link');
    setShareLoading(false);

    if (onLogActivity) {
      onLogActivity('Agreement Shared', formData.refNumber, `Share link generated for ${buyerName} by ${currentUser?.name || 'User'}`);
    }
  };

  const copyLink = (link) => {
    navigator.clipboard.writeText(link || shareLink);
    showToast('Link copied to clipboard!', 'success');
  };

const saveToDatabase = async () => {
  try {
    // First check if agreement already exists
    const { data: existing } = await supabase
      .from('agreements')
      .select('id')
      .eq('ref_number', formData.refNumber)
      .maybeSingle();

    // ── Collect in-person signatures from canvas refs ──
    const inPersonSignatures = {};
    formData.buyers.forEach((_, i) => {
      const canvas = buyerSigRef.current[i];
      if (canvas && !canvas.isEmpty()) {
        inPersonSignatures[i] = canvas.toDataURL('image/png');
      }
    });

    // ── Collect in-person live photos (normalize to plain dataUrl string) ──
    const inPersonPhotos = {};
    Object.entries(buyerPhotos).forEach(([k, v]) => {
      inPersonPhotos[k] = typeof v === 'string' ? v : v?.dataUrl;
    });

    // ── Fetch & merge ALL remote signatures from agreement_signatures table ──
    let mergedRemoteSigs = {};
    let mergedRemotePhotos = {};
    try {
      const { data: remoteRows } = await supabase
        .from('agreement_signatures')
        .select('signatures, photos')
        .eq('status', 'completed')
        .ilike('token', `${formData.refNumber.replace(/\//g, '-')}%`);
      if (remoteRows && remoteRows.length > 0) {
        remoteRows.forEach(row => {
          mergedRemoteSigs = { ...mergedRemoteSigs, ...(row.signatures || {}) };
          mergedRemotePhotos = { ...mergedRemotePhotos, ...(row.photos || {}) };
        });
      }
    } catch (e) {}

    // In-person takes priority over remote; remote fills in any missing buyers
    const finalSignatures = { ...mergedRemoteSigs, ...inPersonSignatures };
    const finalPhotos = { ...mergedRemotePhotos, ...inPersonPhotos };
    const hasSigs = Object.keys(finalSignatures).length > 0;
    const hasPhotos = Object.keys(finalPhotos).length > 0;

    const payload = {
      ref_number: formData.refNumber,
      agreement_type: agreementType,
      date: formData.date,
      currency: formData.currency || 'AED',
      buyer_company: formData.buyers[0]?.buyerCompany || '',
      license_number: formData.buyers[0]?.licenseNumber || '',
      representative_name: formData.buyers[0]?.representativeName || '',
      eid: formData.buyers[0]?.eid || '',
      mobile: formData.buyers[0]?.mobile || '',
      buyers: formData.buyers,
      guarantor_name: formData.guarantorName || null,
      guarantor_eid: formData.guarantorEid || null,
      guarantor_mobile: formData.guarantorMobile || null,
      guarantor_instrument_name: formData.guarantorInstrumentName || null,
      guarantor_instrument_url: formData.guarantorInstrumentUrl || null,
      products: formData.products,
      token_amount: parseFloat(formData.tokenAmount) || 0,
      delivery_amount: deliveryAmount,
      installment_months: parseInt(formData.installmentMonths) || 0,
      monthly_installment: monthlyInstallment,
      subtotal, vat, total,
      remaining_balance: remainingBalance,
      payment_schedule: paymentSchedule,
      created_by: currentUser?.name || null,
      // Save merged signatures & photos so admin panel always shows complete data
      ...(hasSigs && { remote_signatures: finalSignatures }),
      ...(hasPhotos && { remote_photos: finalPhotos }),
    };

    let error;

    if (existing) {
      // Update existing record
      const { error: updateError } = await supabase
        .from('agreements')
        .update(payload)
        .eq('ref_number', formData.refNumber);
      error = updateError;
    } else {
      // Insert new record
      const { error: insertError } = await supabase
        .from('agreements')
        .insert([payload]);
      error = insertError;
    }

    if (error) {
      console.error('Error saving agreement:', error);
      showToast('Warning: Agreement downloaded but could not be saved to admin panel.', 'error');
    } else {
      if (onLogActivity) {
        onLogActivity('Agreement Completed', formData.refNumber, `Agreement saved by ${currentUser?.name || 'User'}`);
      }
    }
  } catch (err) {
    console.error('Unexpected error:', err);
  }
};
  const startCamera = async (index) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      setCameraActive(prev => ({ ...prev, [index]: true }));
      setTimeout(() => {
        const video = document.getElementById(`camera-video-${index}`);
        if (video) video.srcObject = stream;
      }, 100);
    } catch (err) {
      showToast('Could not access camera. Please allow camera permissions.', 'warning');
    }
  };

  const stopCamera = (index) => {
    const video = document.getElementById(`camera-video-${index}`);
    if (video && video.srcObject) {
      video.srcObject.getTracks().forEach(track => track.stop());
      video.srcObject = null;
    }
    setCameraActive(prev => ({ ...prev, [index]: false }));
  };

  const capturePhoto = (index, buyerName) => {
    const video = document.getElementById(`camera-video-${index}`);
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0);
    const dataUrl = canvas.toDataURL('image/jpeg');
    setBuyerPhotos(prev => ({ ...prev, [index]: { dataUrl, buyerName } }));
    stopCamera(index);
  };

  // Builds the agreement PDF and returns the jsPDF doc + filename.
  // Does NOT save the file or write to the DB — that happens on download.
  const buildPdfDoc = async (options = {}) => {
    const clearBtns = document.querySelectorAll('.hide-on-pdf');
    clearBtns.forEach(btn => btn.style.display = 'none');

    // ── FETCH REMOTE SIGNATURES IF AVAILABLE ──
    let remoteSignatures = {};
    let remotePhotos = {};
    try {
      // Search by ref number prefix in token — finds any completed signature for this agreement
      const { data: remoteRows } = await supabase
        .from('agreement_signatures')
        .select('signatures, photos, token')
        .eq('status', 'completed')
        .ilike('token', `${formData.refNumber.replace(/\//g, '-')}%`);
      if (remoteRows && remoteRows.length > 0) {
        remoteRows.forEach(row => {
          remoteSignatures = { ...remoteSignatures, ...(row.signatures || {}) };
          remotePhotos = { ...remotePhotos, ...(row.photos || {}) };
        });
      }
    } catch (e) {}

    const pdf = new jsPDF('p', 'mm', 'a4');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 15;
    const contentWidth = pageWidth - margin * 2;

    // ── PRINT OPTIONS ──
    const skipHeader = !!options.skipHeader;                       // hides the top logo/company header (and its copy badge)
    const skipTransactionFooter = !!options.skipTransactionFooter; // hides the per-page BUYER INITIALS / GUARANTOR footer strip
    const copyTypes = (options.copyTypes && options.copyTypes.length > 0) ? options.copyTypes : ['ORIGINAL'];

    const hasGuarantor = !!showGuarantor;
    const footerReserve = skipTransactionFooter ? 18 : (hasGuarantor ? 52 : 38);

    // Renders one complete copy of the agreement into the shared pdf.
    // Called once per selected copy type; copies after the first start on a fresh page.
    const renderCopy = async (copyType, isFirstCopy) => {
    if (!isFirstCopy) { pdf.addPage(); }
    let y = margin;
    let currentPage = pdf.internal.getNumberOfPages();
    let signaturesStarted = false;

    const checkPageBreak = (neededSpace = 10) => {
      const bottomLimit = signaturesStarted ? pageHeight - margin : pageHeight - footerReserve;
      if (y + neededSpace > bottomLimit) {
        if (!signaturesStarted) drawFooterSignatures(currentPage);
        pdf.addPage(); currentPage++; y = margin;
      }
    };

    const addLine = (color = [220, 216, 207]) => {
      checkPageBreak(5);
      pdf.setDrawColor(...color);
      pdf.line(margin, y, pageWidth - margin, y);
      y += 4;
    };

    const addSectionTitle = (title) => {
      checkPageBreak(12); y += 3;
      pdf.setFillColor(28, 43, 26); pdf.rect(margin, y - 4, contentWidth, 8, 'F');
      pdf.setFontSize(9); pdf.setFont('helvetica', 'bold'); pdf.setTextColor(245, 236, 215);
      pdf.text(title, margin + 3, y + 1); y += 8;
    };

    const drawFooterSignatures = (pageNum) => {
      if (skipTransactionFooter) return;
      const footerY = pageHeight - 35;
      const buyers = formData.buyers;
      const slotWidth = contentWidth / buyers.length;

      // ── GUARANTOR STRIP (above buyer initials) ──
      if (hasGuarantor) {
        const gY = footerY - 17;
        pdf.setDrawColor(200, 169, 110); pdf.setLineWidth(0.4);
        pdf.line(margin, gY, pageWidth - margin, gY); pdf.setLineWidth(0.2);
        pdf.setFontSize(6.5); pdf.setFont('helvetica', 'bold'); pdf.setTextColor(200, 169, 110);
        pdf.text('GUARANTOR:', margin, gY + 5);
        pdf.setFont('helvetica', 'normal'); pdf.setTextColor(50, 50, 50);
        pdf.text(formData.guarantorName || '___________________', margin + 24, gY + 5);
        if (formData.guarantorEid) {
          pdf.text(`CNIC / EID: ${formData.guarantorEid}`, pageWidth / 2, gY + 5);
        }
        pdf.setDrawColor(220, 216, 207); pdf.setLineWidth(0.2);
        pdf.line(margin, gY + 9, pageWidth - margin, gY + 9);
      }

      pdf.setDrawColor(200, 169, 110); pdf.setLineWidth(0.4);
      pdf.line(margin, footerY - 6, pageWidth - margin, footerY - 6); pdf.setLineWidth(0.2);
      pdf.setFontSize(6.5); pdf.setFont('helvetica', 'bold'); pdf.setTextColor(200, 169, 110);
      pdf.text('BUYER INITIALS', margin, footerY - 1);
      buyers.forEach((buyer, index) => {
        const slotCenterX = margin + (index * slotWidth) + slotWidth / 2;
        const sigX = margin + (index * slotWidth) + 4;
        pdf.setFontSize(7); pdf.setFont('helvetica', 'bold'); pdf.setTextColor(28, 43, 26);
        pdf.text(buyers.length > 1 ? `BUYER ${index + 1}:` : 'BUYER:', sigX, footerY + 4);
        pdf.setFont('helvetica', 'normal'); pdf.setTextColor(100, 100, 100);
        pdf.text(buyer.representativeName || '_______________', sigX, footerY + 8);
        const remoteSig = remoteSignatures[index];
        const ref = buyerSigRef.current[index];
        if (remoteSig) {
          const sigImgWidth = 28;
          pdf.addImage(remoteSig, 'PNG', slotCenterX - sigImgWidth / 2, footerY + 10, sigImgWidth, 10);
        } else if (ref && !ref.isEmpty()) {
          const sigImgWidth = 28;
          pdf.addImage(ref.toDataURL('image/png'), 'PNG', slotCenterX - sigImgWidth / 2, footerY + 10, sigImgWidth, 10);
        }
        pdf.setDrawColor(200, 169, 110);
        const lineHalf = (slotWidth - 10) / 2;
        pdf.line(slotCenterX - lineHalf, footerY + 21, slotCenterX + lineHalf, footerY + 21);
        if (index < buyers.length - 1) {
          pdf.setDrawColor(220, 216, 207);
          pdf.line(margin + (index + 1) * slotWidth, footerY - 4, margin + (index + 1) * slotWidth, footerY + 22);
        }
      });
    };

    // ── LOGO + HEADER ──
    if (!skipHeader) {
      try { pdf.addImage(logoBase64, 'JPEG', margin, y, 18, 18); } catch (e) {}
      pdf.setFontSize(16); pdf.setFont('helvetica', 'bold'); pdf.setTextColor(28, 43, 26);
      pdf.text('AL MUSHTARAKA TRADING COMPANY', margin + 22, y + 6);
      pdf.setFontSize(9); pdf.setFont('helvetica', 'normal'); pdf.setTextColor(100, 100, 100);
      pdf.text('DIBBA INDUSTRIAL AREA, UAE', margin + 22, y + 12);
      const badgeWidth = 40;
      const badgeX = pageWidth - margin - badgeWidth;
      pdf.setFillColor(200, 169, 110); pdf.roundedRect(badgeX, y, badgeWidth, 10, 2, 2, 'F');
      pdf.setFontSize(11); pdf.setFont('helvetica', 'bold'); pdf.setTextColor(28, 43, 26);
      pdf.text(copyType, badgeX + badgeWidth / 2, y + 6.5, { align: 'center' });
      y += 22;
    }
    pdf.setFillColor(28, 43, 26); pdf.rect(margin, y, contentWidth, 10, 'F');
    pdf.setFontSize(13); pdf.setFont('helvetica', 'bold'); pdf.setTextColor(245, 236, 215);
    const title = agreementType === 'sale' ? 'SALE AGREEMENT' : 'LEASE CUM SALE AGREEMENT';
    pdf.text(title, pageWidth / 2, y + 7, { align: 'center' }); y += 14;
    pdf.setFontSize(9); pdf.setFont('helvetica', 'normal'); pdf.setTextColor(30, 43, 26);
    pdf.text(`REF: ${formData.refNumber}`, margin, y);
    pdf.text(`DATE: ${formatDate(formData.date)}`, pageWidth - margin, y, { align: 'right' }); y += 8;
    addLine();

    // ── PARTIES ──
    addSectionTitle('PARTIES'); y += 2;
    const col1 = margin; const col2 = pageWidth - margin;
    const sellerStartY = y;
    pdf.setFontSize(9); pdf.setFont('helvetica', 'bold'); pdf.setTextColor(200, 169, 110);
    pdf.setDrawColor(28, 43, 26); pdf.setLineWidth(0.25);
    pdf.roundedRect(col1 - 2, sellerStartY - 3, (pageWidth / 2) - 40, 28, 2, 2, 'S'); pdf.setLineWidth(0.2);
    y += 1; pdf.text('SELLER:-', col1, y); y += 5;
    pdf.setFont('helvetica', 'bold'); pdf.setTextColor(30, 43, 26);
    pdf.text('ALMUSHTARAKA TRADING COMPANY', col1, y); y += 5;
    pdf.setFont('helvetica', 'normal'); pdf.setTextColor(80, 80, 80);
    pdf.text('DIBBA INDUSTRIAL AREA', col1, y); y += 5;
    pdf.text('Representative: ARIF MUKHTAR MALIK', col1, y); y += 5;
    pdf.text('Partner & Manager', col1, y);
    const firstBuyer = formData.buyers[0];
    let buyerRowY = sellerStartY;
    pdf.setFontSize(9); pdf.setFont('helvetica', 'bold'); pdf.setTextColor(200, 169, 110);
    pdf.text(formData.buyers.length > 1 ? 'BUYER 1:-' : 'BUYER:-', col2, buyerRowY, { align: 'right' }); buyerRowY += 5;
    pdf.setFont('helvetica', 'bold'); pdf.setTextColor(30, 43, 26);
    pdf.text(firstBuyer.buyerCompany || '___________________', col2, buyerRowY, { align: 'right' }); buyerRowY += 5;
    pdf.setFont('helvetica', 'normal'); pdf.setTextColor(80, 80, 80);
    pdf.text(`License: ${firstBuyer.licenseNumber || '___________________'}`, col2, buyerRowY, { align: 'right' }); buyerRowY += 5;
    pdf.text(`Rep: ${firstBuyer.representativeName || '___________________'}`, col2, buyerRowY, { align: 'right' }); buyerRowY += 5;
    pdf.text(`EID: ${firstBuyer.eid || '___________________'}`, col2, buyerRowY, { align: 'right' }); buyerRowY += 5;
    pdf.text(`Mobile: ${firstBuyer.mobile || '___________________'}`, col2, buyerRowY, { align: 'right' });
    y = Math.max(y, buyerRowY) + 8;

    const remainingBuyers = formData.buyers.slice(1);
    for (let i = 0; i < remainingBuyers.length; i += 2) {
      checkPageBreak(35);
      const leftBuyer = remainingBuyers[i]; const rightBuyer = remainingBuyers[i + 1];
      const pairStartY = y; let leftY = pairStartY; let rightY = pairStartY;
      pdf.setFontSize(9); pdf.setFont('helvetica', 'bold'); pdf.setTextColor(200, 169, 110);
      pdf.text(`BUYER ${i + 2}:-`, col1, leftY); leftY += 5;
      pdf.setFont('helvetica', 'bold'); pdf.setTextColor(30, 43, 26);
      pdf.text(leftBuyer.buyerCompany || '___________________', col1, leftY); leftY += 5;
      pdf.setFont('helvetica', 'normal'); pdf.setTextColor(80, 80, 80);
      pdf.text(`Rep: ${leftBuyer.representativeName || '___________________'}`, col1, leftY); leftY += 5;
      pdf.text(`EID: ${leftBuyer.eid || '___________________'}`, col1, leftY); leftY += 5;
      pdf.text(`Mobile: ${leftBuyer.mobile || '___________________'}`, col1, leftY);
      if (rightBuyer) {
        pdf.setFontSize(9); pdf.setFont('helvetica', 'bold'); pdf.setTextColor(200, 169, 110);
        pdf.text(`BUYER ${i + 3}:-`, col2, rightY, { align: 'right' }); rightY += 5;
        pdf.setFont('helvetica', 'bold'); pdf.setTextColor(30, 43, 26);
        pdf.text(rightBuyer.buyerCompany || '___________________', col2, rightY, { align: 'right' }); rightY += 5;
        pdf.setFont('helvetica', 'normal'); pdf.setTextColor(80, 80, 80);
        pdf.text(`Rep: ${rightBuyer.representativeName || '___________________'}`, col2, rightY, { align: 'right' }); rightY += 5;
        pdf.text(`EID: ${rightBuyer.eid || '___________________'}`, col2, rightY, { align: 'right' }); rightY += 5;
        pdf.text(`Mobile: ${rightBuyer.mobile || '___________________'}`, col2, rightY, { align: 'right' });
      }
      y = Math.max(leftY, rightY) + 8;
    }

    if (hasGuarantor) {
      addSectionTitle('GUARANTOR'); y += 2;
      pdf.setFontSize(9); pdf.setFont('helvetica', 'normal'); pdf.setTextColor(80, 80, 80);
      pdf.text(`Name: ${formData.guarantorName || '___________________'}`, margin, y); y += 5;
      pdf.text(`CNIC / EID: ${formData.guarantorEid || '___________________'}`, margin, y); y += 5;
      pdf.text(`Mobile: ${formData.guarantorMobile || '___________________'}`, margin, y); y += 8;
    }

    addSectionTitle('PRODUCTS'); y += 2;
    const colWidths = [10, 65, 15, 15, 25, 15, 25];
    const colX = colWidths.reduce((acc, w, i) => { acc.push(i === 0 ? margin : acc[i - 1] + colWidths[i - 1]); return acc; }, []);
    pdf.setFillColor(45, 74, 42); pdf.rect(margin, y - 4, contentWidth, 8, 'F');
    pdf.setFontSize(8); pdf.setFont('helvetica', 'bold'); pdf.setTextColor(245, 236, 215);
    ['SR', 'PRODUCT', 'QTY', 'UNIT', 'RATE', 'CRNCY', 'AMOUNT'].forEach((h, i) => pdf.text(h, colX[i] + 1, y + 1));
    y += 7;
    formData.products.forEach((p, i) => {
      checkPageBreak(8);
      if (i % 2 === 0) { pdf.setFillColor(247, 244, 239); pdf.rect(margin, y - 4, contentWidth, 7, 'F'); }
      pdf.setFontSize(8); pdf.setFont('helvetica', 'normal'); pdf.setTextColor(30, 43, 26);
      const amount = (parseFloat(p.qty) * parseFloat(p.rate) || 0).toFixed(2);
      [String(i + 1), p.name, String(p.qty), p.unit, parseFloat(p.rate).toFixed(2), cur, amount].forEach((d, j) => pdf.text(String(d), colX[j] + 1, y));
      y += 7;
    });
    checkPageBreak(8);
    pdf.setFillColor(245, 236, 215); pdf.rect(margin, y - 4, contentWidth, 7, 'F');
    pdf.setFont('helvetica', 'bold'); pdf.setTextColor(30, 43, 26);
    pdf.text('VALUE FOR VAT', colX[1] + 1, y); pdf.text(subtotal.toFixed(2), colX[6] + 1, y); y += 7;
    checkPageBreak(8); pdf.setFont('helvetica', 'normal');
    pdf.text('VAT 5%', colX[5] + 1, y); pdf.text(vat.toFixed(2), colX[6] + 1, y); y += 7;
    checkPageBreak(8);
    pdf.setFillColor(28, 43, 26); pdf.rect(margin, y - 4, contentWidth, 8, 'F');
    pdf.setFont('helvetica', 'bold'); pdf.setTextColor(245, 236, 215);
    pdf.text('TOTAL AMOUNT INCLUDING VAT', margin + 3, y + 1);
    pdf.text(`${cur} ${total.toFixed(2)}`, colX[6] + 1, y + 1); y += 12;

    addSectionTitle('PAYMENT TERMS'); y += 2;
    pdf.setFontSize(9); pdf.setFont('helvetica', 'normal'); pdf.setTextColor(80, 80, 80);
    [
      `I-   At the time of booking / Token Money, CASH ${cur} = ${formData.tokenAmount}/-`,
      `II-  At the time of delivery of goods, ${cur}: ${deliveryAmount.toFixed(2)}/-`,
      `III- Remaining balance ${cur} ${remainingBalance.toFixed(2)} to be paid in ${formData.installmentMonths} monthly installments of ${cur} ${monthlyInstallment.toFixed(2)} per month.`,
      `IV-  Installments start from ${paymentSchedule.length > 0 ? paymentSchedule[0].dueDate : ''}. Due date is ${paymentSchedule.length > 0 ? paymentSchedule[0].dueDate.split(' ')[0] : '5th'} of each month.`,
    ].forEach(line => {
      pdf.splitTextToSize(line, contentWidth).forEach(l => { checkPageBreak(6); pdf.text(l, margin, y); y += 5; });
    });
    if (extraPaymentTerms && extraPaymentTerms.length > 0) {
      const romans = ['V', 'VI', 'VII', 'VIII', 'IX', 'X'];
      extraPaymentTerms.filter(t => t.trim()).forEach((term, index) => {
        pdf.splitTextToSize(`${romans[index]}- ${term}`, contentWidth).forEach(l => { checkPageBreak(6); pdf.text(l, margin, y); y += 5; });
      });
    }

    if (paymentSchedule.length > 0) {
      addSectionTitle('PAYMENT SCHEDULE'); y += 2;
      const schedColX = [margin, margin + 20, margin + 70, margin + 120];
      checkPageBreak(15);
      pdf.setFillColor(45, 74, 42); pdf.rect(margin, y - 4, contentWidth, 8, 'F');
      pdf.setFontSize(8); pdf.setFont('helvetica', 'bold'); pdf.setTextColor(245, 236, 215);
      ['MONTH', 'PERIOD', 'DUE DATE', `AMOUNT (${cur})`].forEach((h, i) => pdf.text(h, schedColX[i] + 1, y + 1)); y += 7;
      paymentSchedule.forEach((row, i) => {
        checkPageBreak(10);
        if (i % 2 === 0) { pdf.setFillColor(247, 244, 239); pdf.rect(margin, y - 4, contentWidth, 7, 'F'); }
        pdf.setFontSize(8); pdf.setFont('helvetica', 'normal'); pdf.setTextColor(30, 43, 26);
        [String(row.month), row.label, row.dueDate, `${cur} ${row.amount}`].forEach((d, j) => pdf.text(d, schedColX[j] + 1, y)); y += 7;
      }); y += 5;
    }

    addSectionTitle('STANDARD TERMS & CONDITIONS'); y += 3;
    ["01- The buyer is bound to examine the goods on arrival and in case of any discrepancy / claim regarding quality / grade of products / machinery, should be informed in writing to seller within TWO (2) working days. The failure of the buyer to comply with this rule, should be considered acceptance of product / machinery in good condition / without any fault or claim.",
      "02- In case of delay or failure of payment, warranty of products should be deemed as cancelled until / unless pending payments should be cleared in favour of seller.",
      "03- Payment of the agreement shall be made within Credit period agreed between us as per credit facility terms.",
      "04- The goods sold under this agreement will remain the property of M/S Almushtaraka Trading Company only. Unless and until the full payment of such goods has been settled to Almushtaraka Trading Company.",
      "05- If buyer fails to pay the full amount in agreed period, seller has the right to take back his goods, mentioned in this sales agreement.",
      "06- In case of sale or transfer of business entity / ownership of goods or machinery supplied under this agreement, the BUYER is bound to inform the SELLER and settle his outstanding dues with seller.",
    ].forEach(term => {
      pdf.splitTextToSize(term, contentWidth).forEach(l => { checkPageBreak(6); pdf.setFontSize(8.5); pdf.setFont('helvetica', 'normal'); pdf.setTextColor(60, 60, 60); pdf.text(l, margin, y); y += 5; });
    });
    if (extraStandardTerms && extraStandardTerms.length > 0) {
      extraStandardTerms.filter(t => t.trim()).forEach((term, index) => {
        pdf.splitTextToSize(`${String(index + 7).padStart(2, '0')}- ${term}`, contentWidth).forEach(l => { checkPageBreak(6); pdf.setFontSize(8.5); pdf.setFont('helvetica', 'normal'); pdf.setTextColor(60, 60, 60); pdf.text(l, margin, y); y += 5; });
      });
    }

    // ── SIGNATURES — dedicated page (all signatures together) ──
    // Draw footer on the last content page before we leave it
    drawFooterSignatures(currentPage);
    pdf.addPage(); currentPage++;
    y = margin;
    signaturesStarted = true; // overflow pages from here won't get the footer initials strip

    // Page header
    pdf.setFillColor(28, 43, 26); pdf.rect(margin, y, contentWidth, 10, 'F');
    pdf.setFontSize(13); pdf.setFont('helvetica', 'bold'); pdf.setTextColor(245, 236, 215);
    pdf.text('SIGNATURES', pageWidth / 2, y + 7, { align: 'center' }); y += 16;
    pdf.setDrawColor(200, 169, 110); pdf.setLineWidth(0.4);
    pdf.line(margin, y, pageWidth - margin, y); pdf.setLineWidth(0.2);
    y += 10;

    const allSignatories = [
      { label: 'SELLER', name: 'ARIF MUKHTAR MALIK', sub: 'Partner & Manager', eid: null, isSeller: true },
      ...formData.buyers.map((buyer, index) => ({ label: formData.buyers.length > 1 ? `BUYER ${index + 1}` : 'BUYER', name: buyer.representativeName || '___________________', sub: null, eid: buyer.eid || '___________________', isSeller: false, buyerIndex: index })),
      ...(hasGuarantor ? [{ label: 'GUARANTOR', name: formData.guarantorName || '___________________', sub: null, eid: formData.guarantorEid || '___________________', isGuarantor: true }] : []),
    ];
    for (let i = 0; i < allSignatories.length; i += 2) {
      // On this dedicated page, allow full page height; only break if genuinely overflowing
      if (y + 52 > pageHeight - margin) { pdf.addPage(); currentPage++; y = margin; }
      const left = allSignatories[i]; const right = allSignatories[i + 1];
      const rowStartY = y;
      pdf.setFontSize(9); pdf.setFont('helvetica', 'bold'); pdf.setTextColor(200, 169, 110);
      pdf.text(`${left.label}:`, col1, rowStartY);
      pdf.setFont('helvetica', 'normal'); pdf.setTextColor(30, 43, 26);
      pdf.text(left.name, col1, rowStartY + 5); pdf.setTextColor(100, 100, 100);
      if (left.sub) pdf.text(left.sub, col1, rowStartY + 10);
      if (left.eid) pdf.text(`${left.isGuarantor ? 'CNIC/EID' : 'EID'}: ${left.eid}`, col1, rowStartY + 10);
      if (left.isSeller) {
        try { const s = require('../assets/seller-signature.jpg'); pdf.addImage(s, 'JPEG', col1, rowStartY + 14, 50, 20); } catch (e) {}
      } else if (left.isGuarantor) {
        const ref = guarantorSigRef.current;
        if (ref && !ref.isEmpty()) {
          pdf.addImage(ref.toDataURL('image/png'), 'PNG', col1, rowStartY + 14, 50, 20);
        }
      } else {
        // Use remote signature if available, otherwise local canvas
        const remoteSig = remoteSignatures[left.buyerIndex];
        const ref = buyerSigRef.current[left.buyerIndex];
        if (remoteSig) {
          pdf.addImage(remoteSig, 'PNG', col1, rowStartY + 14, 50, 20);
        } else if (ref && !ref.isEmpty()) {
          pdf.addImage(ref.toDataURL('image/png'), 'PNG', col1, rowStartY + 14, 50, 20);
        }
      }
      pdf.setDrawColor(200, 169, 110); pdf.line(col1, rowStartY + 38, col1 + 55, rowStartY + 38);
      pdf.setFontSize(7); pdf.setTextColor(150, 150, 150); pdf.text(`${left.label} Signature`, col1, rowStartY + 42);
      if (right) {
        pdf.setFontSize(9); pdf.setFont('helvetica', 'bold'); pdf.setTextColor(200, 169, 110);
        pdf.text(`${right.label}:`, col2, rowStartY, { align: 'right' });
        pdf.setFont('helvetica', 'normal'); pdf.setTextColor(30, 43, 26);
        pdf.text(right.name, col2, rowStartY + 5, { align: 'right' }); pdf.setTextColor(100, 100, 100);
        if (right.sub) pdf.text(right.sub, col2, rowStartY + 10, { align: 'right' });
        if (right.eid) pdf.text(`${right.isGuarantor ? 'CNIC/EID' : 'EID'}: ${right.eid}`, col2, rowStartY + 10, { align: 'right' });
        if (right.isSeller) {
          try { const s = require('../assets/seller-signature.jpg'); pdf.addImage(s, 'JPEG', col2 - 50, rowStartY + 14, 50, 20); } catch (e) {}
        } else if (right.isGuarantor) {
          const ref = guarantorSigRef.current;
          if (ref && !ref.isEmpty()) {
            pdf.addImage(ref.toDataURL('image/png'), 'PNG', col2 - 50, rowStartY + 14, 50, 20);
          }
        } else {
          // Use remote signature if available, otherwise local canvas
          const remoteSig = remoteSignatures[right.buyerIndex];
          const ref = buyerSigRef.current[right.buyerIndex];
          if (remoteSig) {
            pdf.addImage(remoteSig, 'PNG', col2 - 50, rowStartY + 14, 50, 20);
          } else if (ref && !ref.isEmpty()) {
            pdf.addImage(ref.toDataURL('image/png'), 'PNG', col2 - 50, rowStartY + 14, 50, 20);
          }
        }
        pdf.setDrawColor(200, 169, 110); pdf.line(col2 - 55, rowStartY + 38, col2, rowStartY + 38);
        pdf.setFontSize(7); pdf.setTextColor(150, 150, 150); pdf.text(`${right.label} Signature / Stamp`, col2, rowStartY + 42, { align: 'right' });
      }
      y = rowStartY + 52;
    }

    // ── CUSTOMER IDs PAGE ── 3 buyers per page (6 ID cards: front+back per row)
    const buyersWithIds = formData.buyers.filter(b => b.idFrontUrl || b.idBackUrl);
    if (buyersWithIds.length > 0) {
      pdf.addPage(); currentPage++; drawFooterSignatures(currentPage);
      let idY = margin;
      pdf.setFillColor(28, 43, 26); pdf.rect(margin, idY, contentWidth, 10, 'F');
      pdf.setFontSize(13); pdf.setFont('helvetica', 'bold'); pdf.setTextColor(245, 236, 215);
      pdf.text('CUSTOMER IDENTIFICATION DOCUMENTS', pageWidth / 2, idY + 7, { align: 'center' }); idY += 18;
      const imgWidth = (contentWidth - 10) / 2;
      const imgHeight = 50; // compact height — 3 rows fit per page
      const rowGap = 8;
      const pageLimit = pageHeight - margin - footerReserve;
      for (let i = 0; i < buyersWithIds.length; i++) {
        const buyer = buyersWithIds[i];
        // Need: 6 (name) + imgHeight for this row
        if (idY + 6 + imgHeight > pageLimit) {
          pdf.addPage(); currentPage++; drawFooterSignatures(currentPage); idY = margin;
        }
        pdf.setFontSize(8.5); pdf.setFont('helvetica', 'bold'); pdf.setTextColor(28, 43, 26);
        pdf.text(buyer.representativeName || `Buyer ${i + 1}`, margin, idY); idY += 5;
        const sides = [{ url: buyer.idFrontUrl, label: 'FRONT' }, { url: buyer.idBackUrl, label: 'BACK' }];
        for (let s = 0; s < sides.length; s++) {
          const xPos = s === 0 ? margin : margin + imgWidth + 10; const { url, label } = sides[s];
          pdf.setFontSize(6.5); pdf.setFont('helvetica', 'bold'); pdf.setTextColor(200, 169, 110); pdf.text(label, xPos, idY);
          pdf.setDrawColor(200, 169, 110); pdf.setLineWidth(0.4); pdf.roundedRect(xPos, idY + 2, imgWidth, imgHeight, 2, 2, 'S'); pdf.setLineWidth(0.2);
          if (url) {
            try {
              const img = new Image(); img.crossOrigin = 'anonymous';
              await new Promise((resolve) => { img.onload = resolve; img.onerror = resolve; img.src = url; });
              const canvas = document.createElement('canvas'); canvas.width = img.naturalWidth || 300; canvas.height = img.naturalHeight || 200;
              canvas.getContext('2d').drawImage(img, 0, 0);
              pdf.addImage(canvas.toDataURL('image/jpeg'), 'JPEG', xPos + 2, idY + 4, imgWidth - 4, imgHeight - 4);
            } catch (e) {}
          } else { pdf.setFontSize(8); pdf.setTextColor(180, 180, 180); pdf.text('Not uploaded', xPos + imgWidth / 2, idY + imgHeight / 2 + 2, { align: 'center' }); }
        }
        idY += imgHeight + rowGap;
      }
    }

    // ── BUYER PHOTOS PAGE — merge local + remote ──
    const mergedPhotos = { ...remotePhotos };
    Object.entries(buyerPhotos).forEach(([k, v]) => { mergedPhotos[k] = v; });
    const photoEntries = Object.entries(mergedPhotos);
    if (photoEntries.length > 0) {
      pdf.addPage(); currentPage++; drawFooterSignatures(currentPage);
      let photoY = margin;
      pdf.setFillColor(28, 43, 26); pdf.rect(margin, photoY, contentWidth, 10, 'F');
      pdf.setFontSize(13); pdf.setFont('helvetica', 'bold'); pdf.setTextColor(245, 236, 215);
      pdf.text('BUYER PHOTOGRAPHS', pageWidth / 2, photoY + 7, { align: 'center' }); photoY += 18;
      const photoWidth = (contentWidth - 10) / 2; const photoHeight = 70;
      const cols = [margin, margin + photoWidth + 10];
      for (let i = 0; i < photoEntries.length; i++) {
        const [buyerIndex, photo] = photoEntries[i];
        const col = cols[i % 2];
        if (i % 2 === 0 && i > 0) photoY += photoHeight + 24;
        if (photoY + photoHeight + 30 > pageHeight - margin - 30) { pdf.addPage(); currentPage++; drawFooterSignatures(currentPage); photoY = margin; }
        pdf.setFontSize(9); pdf.setFont('helvetica', 'bold'); pdf.setTextColor(200, 169, 110);
        const buyerName = formData.buyers[parseInt(buyerIndex)]?.representativeName || `Buyer ${parseInt(buyerIndex) + 1}`;
        pdf.text(buyerName, col, photoY);
        pdf.setDrawColor(200, 169, 110); pdf.setLineWidth(0.4); pdf.roundedRect(col, photoY + 3, photoWidth, photoHeight, 2, 2, 'S'); pdf.setLineWidth(0.2);
        // photo can be a string (remote) or object with .dataUrl (local)
        const photoDataUrl = typeof photo === 'string' ? photo : photo?.dataUrl;
        if (photoDataUrl) {
          try { pdf.addImage(photoDataUrl, 'JPEG', col + 2, photoY + 5, photoWidth - 4, photoHeight - 4); } catch (e) {}
        }
      }
    }

    // ── GUARANTEE INSTRUMENT PAGE ──
    if (showGuarantor && formData.guarantorInstrumentUrl) {
      pdf.addPage(); currentPage++; drawFooterSignatures(currentPage);
      let instY = margin;
      pdf.setFillColor(28, 43, 26); pdf.rect(margin, instY, contentWidth, 10, 'F');
      pdf.setFontSize(13); pdf.setFont('helvetica', 'bold'); pdf.setTextColor(245, 236, 215);
      pdf.text('GUARANTEE INSTRUMENT', pageWidth / 2, instY + 7, { align: 'center' }); instY += 18;
      if (formData.guarantorInstrumentName) {
        pdf.setFontSize(10); pdf.setFont('helvetica', 'bold'); pdf.setTextColor(200, 169, 110);
        pdf.text(`Instrument: ${formData.guarantorInstrumentName}`, margin, instY); instY += 8;
        pdf.setFontSize(9); pdf.setFont('helvetica', 'normal'); pdf.setTextColor(80, 80, 80);
        pdf.text(`Guarantor: ${formData.guarantorName || '—'}`, margin, instY); instY += 10;
      }
      try {
        const img = new Image(); img.crossOrigin = 'anonymous';
        await new Promise((resolve) => { img.onload = resolve; img.onerror = resolve; img.src = formData.guarantorInstrumentUrl; });
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth || 600; canvas.height = img.naturalHeight || 400;
        canvas.getContext('2d').drawImage(img, 0, 0);
        const availH = pageHeight - instY - margin - footerReserve;
        const availW = contentWidth;
        const ratio = Math.min(availW / canvas.width, availH / canvas.height, 1);
        const drawW = canvas.width * ratio;
        const drawH = canvas.height * ratio;
        const drawX = margin + (availW - drawW) / 2;
        pdf.setDrawColor(200, 169, 110); pdf.setLineWidth(0.4);
        pdf.roundedRect(drawX - 2, instY - 2, drawW + 4, drawH + 4, 2, 2, 'S');
        pdf.addImage(canvas.toDataURL('image/jpeg'), 'JPEG', drawX, instY, drawW, drawH);
      } catch (e) {}
    }

    }; // ── end renderCopy ──

    // Draw each selected copy in sequence into the same PDF.
    for (let c = 0; c < copyTypes.length; c++) {
      await renderCopy(copyTypes[c], c === 0);
    }

    // ── PAGE NUMBERS (top-right, above all content) ──
    const totalPages = pdf.internal.getNumberOfPages();
    for (let p = 1; p <= totalPages; p++) {
      pdf.setPage(p);
      pdf.setFontSize(7.5);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(160, 160, 160);
      pdf.text(`Page ${p} of ${totalPages}`, pageWidth - margin, 9, { align: 'right' });
    }

    const labelMap = { 'ORIGINAL': 'Original', 'OFFICE COPY': 'OfficeCopy', 'CUSTOMER COPY': 'CustomerCopy', 'DUPLICATE': 'Duplicate' };
    const copyLabel = copyTypes.map(c => labelMap[c] || 'Copy').join('-');
    const plainSuffix = skipHeader ? '-Plain' : '';
    const filename = `Agreement-${formData.refNumber}-${copyLabel}${plainSuffix}.pdf`;
    clearBtns.forEach(btn => btn.style.display = 'block');
    return { pdf, filename };
  };

  // Build the PDF from the chosen options and show it in an in-page preview.
  const handleGeneratePreview = async () => {
    if (isGenerating.current) return;
    isGenerating.current = true;
    setPreviewBuilding(true);
    try {
      const copyTypes = [];
      if (printOptions.original) copyTypes.push('ORIGINAL');
      if (printOptions.duplicate) copyTypes.push('DUPLICATE');
      if (printOptions.office) copyTypes.push('OFFICE COPY');
      if (printOptions.customer) copyTypes.push('CUSTOMER COPY');
      const { pdf, filename } = await buildPdfDoc({
        copyTypes,
        skipHeader: printOptions.hideHeader,
        skipTransactionFooter: printOptions.hideFooter,
      });
      if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
      const url = URL.createObjectURL(pdf.output('blob'));
      previewDocRef.current = { pdf, filename };
      previewUrlRef.current = url;
      setPreviewUrl(url);
      setShowPrintModal(false);
    } catch (e) {
      showToast('Could not generate preview. Please try again.', 'error');
    }
    setPreviewBuilding(false);
    isGenerating.current = false;
  };

  const closePreview = () => {
    if (previewUrlRef.current) { URL.revokeObjectURL(previewUrlRef.current); previewUrlRef.current = null; }
    previewDocRef.current = null;
    setPreviewUrl(null);
  };

  // Confirm from the preview → actually download the file and save to the admin panel.
  const handleDownloadFromPreview = async () => {
    if (!previewDocRef.current) return;
    const { pdf, filename } = previewDocRef.current;
    pdf.save(filename);
    await saveToDatabase();
    showToast('Agreement downloaded.', 'success');
    closePreview();
  };

  return (
    <div className="preview-wrapper">
      {ToastComponent}

      {showShareModal && (
        <div className="modal-overlay">
          <div className="modal-card share-modal">

            {shareModalStep === 'select' && (
              <>
                <h3>🔗 Share Agreement for Signing</h3>
                <p className="share-modal-hint">Select which buyer you want to send a signing link to:</p>
                <div className="share-buyer-list">
                  {formData.buyers.map((buyer, i) => (
                    <div
                      key={i}
                      className={`share-buyer-option${selectedShareBuyer === i ? ' selected' : ''}${generatedLinks[i] ? ' already-shared' : ''}`}
                      onClick={() => setSelectedShareBuyer(i)}
                    >
                      <div className="share-buyer-avatar">{i + 1}</div>
                      <div className="share-buyer-info">
                        <span className="share-buyer-name">{buyer.representativeName || `Buyer ${i + 1}`}</span>
                        <span className="share-buyer-company">{buyer.buyerCompany || '—'}</span>
                      </div>
                      {generatedLinks[i] && <span className="share-link-badge">🔗 Sent</span>}
                    </div>
                  ))}
                </div>
                <div className="modal-actions">
                  <button className="btn-modal-cancel" onClick={() => setShowShareModal(false)}>Cancel</button>
                  <button
                    className="btn-modal-save"
                    onClick={() => generateShareLink(selectedShareBuyer)}
                    disabled={selectedShareBuyer === null || shareLoading}
                  >
                    {shareLoading ? '⏳ Generating...' : '🔗 Generate Link'}
                  </button>
                </div>
              </>
            )}

            {shareModalStep === 'link' && (
              <>
                <h3>✅ Link Generated</h3>
                <p className="share-modal-hint">
                  Send this link to <strong>{formData.buyers[selectedShareBuyer]?.representativeName || `Buyer ${selectedShareBuyer + 1}`}</strong> to sign their part of the agreement.
                </p>
                <div className="share-link-box">
                  <span className="share-link-text">{shareLink}</span>
                  <button className="btn-copy-inline" onClick={() => copyLink(shareLink)}>📋 Copy</button>
                </div>
                <div className="modal-actions">
                  {formData.buyers.length > 1 && (
                    <button className="btn-modal-cancel" onClick={() => { setShareModalStep('select'); setSelectedShareBuyer(null); }}>
                      ← Share Another Buyer
                    </button>
                  )}
                  <button className="btn-modal-save" onClick={() => setShowShareModal(false)}>Done</button>
                </div>
              </>
            )}

          </div>
        </div>
      )}

      {showPrintModal && (
        <div className="modal-overlay">
          <div className="modal-card print-modal">
            <h3>🖨 Print Agreement</h3>
            <p className="share-modal-hint">Tick the options you want, then download the PDF.</p>

            <div className="print-options">
              <div className="print-options-group">
                <p className="print-options-heading">Layout</p>
                <label className="print-option">
                  <input
                    type="checkbox"
                    checked={printOptions.hideHeader}
                    onChange={(e) => setPrintOptions(o => ({ ...o, hideHeader: e.target.checked }))}
                  />
                  <span>Hide Header</span>
                </label>
                <label className="print-option">
                  <input
                    type="checkbox"
                    checked={printOptions.hideFooter}
                    onChange={(e) => setPrintOptions(o => ({ ...o, hideFooter: e.target.checked }))}
                  />
                  <span>Hide Footer</span>
                </label>
              </div>

              <div className="print-options-group">
                <p className="print-options-heading">Copy Type</p>
                <label className="print-option">
                  <input
                    type="checkbox"
                    checked={printOptions.office}
                    onChange={(e) => setPrintOptions(o => ({ ...o, office: e.target.checked }))}
                  />
                  <span>Office Copy</span>
                </label>
                <label className="print-option">
                  <input
                    type="checkbox"
                    checked={printOptions.customer}
                    onChange={(e) => setPrintOptions(o => ({ ...o, customer: e.target.checked }))}
                  />
                  <span>Customer Copy</span>
                </label>
                <label className="print-option">
                  <input
                    type="checkbox"
                    checked={printOptions.original}
                    onChange={(e) => setPrintOptions(o => ({ ...o, original: e.target.checked }))}
                  />
                  <span>Original</span>
                </label>
                <label className="print-option">
                  <input
                    type="checkbox"
                    checked={printOptions.duplicate}
                    onChange={(e) => setPrintOptions(o => ({ ...o, duplicate: e.target.checked }))}
                  />
                  <span>Duplicate</span>
                </label>
              </div>
            </div>

            <p className="print-hint-small">
              Pick more than one copy type to print them together in a single PDF. If none is selected, a single Original is printed.
            </p>

            <div className="modal-actions">
              <button className="btn-modal-cancel" onClick={() => setShowPrintModal(false)}>Cancel</button>
              <button className="btn-modal-save" onClick={handleGeneratePreview} disabled={previewBuilding}>
                {previewBuilding ? '⏳ Generating…' : '👁 Preview'}
              </button>
            </div>
          </div>
        </div>
      )}

      {previewUrl && (
        <div className="modal-overlay">
          <div className="modal-card preview-pdf-modal">
            <div className="preview-pdf-head">
              <h3>📄 Print Preview</h3>
              <button className="preview-pdf-close" onClick={closePreview} title="Close">✕</button>
            </div>
            <p className="share-modal-hint">Review the document below, then download to print.</p>
            <div className="preview-pdf-frame-wrap">
              <iframe title="Agreement Print Preview" src={previewUrl} className="preview-pdf-frame" />
            </div>
            <div className="modal-actions">
              <button className="btn-modal-cancel" onClick={() => { closePreview(); setShowPrintModal(true); }}>← Back to Options</button>
              <button className="btn-modal-save" onClick={handleDownloadFromPreview}>⬇ Download / Print</button>
            </div>
          </div>
        </div>
      )}

      <div className="preview-actions-top">
        <button className="btn-back-preview" onClick={onBack}>← Edit Form</button>
        <div className="preview-actions-right">
          <button className="btn-share-link" onClick={openShareModal}>
            🔗 Share for Signing
          </button>
          <button className="btn-generate-pdf btn-print" onClick={() => setShowPrintModal(true)}>🖨 Print</button>
        </div>
      </div>

      <div id="agreement-preview-content" className="preview-document">
        <div className="preview-header">
          <div className="preview-header-left">
            <img src={logoBase64} alt="Al Mushtaraka Logo" className="agreement-logo" />
            <div><h1>ALMUSHTARAKA TRADING COMPANY</h1><p>DIBBA INDUSTRIAL AREA</p></div>
          </div>
          <div className="preview-header-right"><div className="preview-copy-badge">ORIGINAL</div></div>
        </div>

        <div className="preview-title">
          <h2>{agreementType === 'sale' ? 'SALE AGREEMENT' : 'LEASE CUM SALE AGREEMENT'}</h2>
        </div>

        <div className="preview-ref-row">
          <span><strong>REF:</strong> {formData.refNumber}</span>
          <span><strong>DATE:</strong> {formatDate(formData.date)}</span>
        </div>

        <div className="preview-parties-grid">
          <div className="preview-party">
            <p className="party-label">SELLER:-</p>
            <p><strong>ALMUSHTARAKA TRADING COMPANY</strong></p>
            <p>DIBBA INDUSTRIAL AREA</p>
            <p>Representative: ARIF MUKHTAR MALIK</p>
            <p>Partner & Manager</p>
          </div>
          {formData.buyers.map((buyer, index) => (
            <div className="preview-party" key={index}>
              <p className="party-label">{formData.buyers.length > 1 ? `BUYER ${index + 1}:-` : 'BUYER:-'}</p>
              <p><strong>{buyer.buyerCompany || '___________________'}</strong></p>
              {index === 0 && <p>License Number: {buyer.licenseNumber || '___________________'}</p>}
              <p>Representative: {buyer.representativeName || '___________________'}</p>
              <p>ID#: {buyer.eid || '___________________'}</p>
              <p>Mobile: {buyer.mobile || '___________________'}</p>
            </div>
          ))}
        </div>

        <table className="preview-table">
          <thead><tr><th>SR.</th><th>PRODUCTS</th><th>QTY</th><th>UNIT</th><th>RATE / UNIT</th><th>CRNCY</th><th>AMOUNT</th></tr></thead>
          <tbody>
            {formData.products.map((p, i) => (
              <tr key={i}>
                <td>{i + 1}</td><td>{p.name}</td><td>{p.qty}</td><td>{p.unit}</td>
                <td>{parseFloat(p.rate).toFixed(2)}</td><td>{cur}</td>
                <td>{((parseFloat(p.qty) * parseFloat(p.rate)) || 0).toFixed(2)}</td>
              </tr>
            ))}
            <tr className="total-row-preview"><td colSpan="6">VALUE FOR VAT</td><td>{subtotal.toFixed(2)}</td></tr>
            <tr className="total-row-preview"><td colSpan="5"></td><td>VAT 5%</td><td>{vat.toFixed(2)}</td></tr>
            <tr className="total-row-final"><td colSpan="4"><strong>TOTAL AMOUNT INCLUDING VAT</strong></td><td colSpan="3"><strong>{cur} {total.toFixed(2)}</strong></td></tr>
          </tbody>
        </table>

        <div className="preview-section">
          <p className="section-title">Payment Terms:-</p>
          <p>I- At the time of booking / Token Money, CASH {cur} = {formData.tokenAmount}/-</p>
          <p>II- At the time of delivery of goods, {cur}: {deliveryAmount.toFixed(2)}/-</p>
          <p>III- Remaining balance {cur} {remainingBalance.toFixed(2)} to be paid in {formData.installmentMonths} monthly installments of {cur} {monthlyInstallment.toFixed(2)} per month.</p>
          <p>IV- Installments start from {paymentSchedule.length > 0 ? paymentSchedule[0].dueDate : ''}. Due date is {paymentSchedule.length > 0 ? paymentSchedule[0].dueDate.split(' ')[0] : '5th'} of each month.</p>
          {extraPaymentTerms && extraPaymentTerms.filter(t => t.trim()).map((term, index) => (
            <p key={index}>{['V', 'VI', 'VII', 'VIII', 'IX', 'X'][index]}- {term}</p>
          ))}
        </div>

        {paymentSchedule.length > 0 && (
          <div className="preview-section">
            <p className="section-title">Payment Schedule:-</p>
            <table className="preview-table">
              <thead><tr><th>Month</th><th>Period</th><th>Due Date</th><th>Amount ({cur})</th></tr></thead>
              <tbody>
                {paymentSchedule.map((row) => (
                  <tr key={row.month}><td>{row.month}</td><td>{row.label}</td><td>{row.dueDate}</td><td>{cur} {row.amount}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="preview-section">
          <p className="section-title">Standard Terms & Conditions:-</p>
          {terms.map((term, i) => (
            <p key={i} className="term-item"><strong>{String(i + 1).padStart(2, '0')}-</strong> {term}</p>
          ))}
          {extraStandardTerms && extraStandardTerms.filter(t => t.trim()).map((term, index) => (
            <p key={index} className="term-item"><strong>{String(index + 7).padStart(2, '0')}-</strong> {term}</p>
          ))}
        </div>

        {showGuarantor && (
          <div className="preview-section">
            <p className="section-title">Guarantor:-</p>
            <p>Name: {formData.guarantorName || '___________________'}</p>
            <p>CNIC / EID: {formData.guarantorEid || '___________________'}</p>
            <p>Mobile: {formData.guarantorMobile || '___________________'}</p>
            {formData.guarantorInstrumentName && (
              <p>Guarantee Instrument: <strong>{formData.guarantorInstrumentName}</strong></p>
            )}
            {formData.guarantorInstrumentUrl && (
              <div className="preview-instrument-thumb">
                <img src={formData.guarantorInstrumentUrl} alt="Guarantee Instrument" />
              </div>
            )}
          </div>
        )}

        {showGuarantor && (
          <div className="preview-guarantor-strip">
            <span className="guarantor-strip-label">GUARANTOR:</span>
            <span className="guarantor-strip-name">{formData.guarantorName || '___________________'}</span>
            <span className="guarantor-strip-sep">|</span>
            <span className="guarantor-strip-id">CNIC / EID: {formData.guarantorEid || '___________________'}</span>
          </div>
        )}

        <div className="preview-signatures-grid">
          <div className="sig-box">
            <p className="sig-label">SELLER:</p>
            <p>ARIF MUKHTAR MALIK</p><p>Partner & Manager</p>
            <p className="sig-label" style={{ marginTop: '10px' }}>Signature:</p>
            <img src={sellerSignature} alt="Seller Signature" className="seller-sig-img" />
          </div>
          {formData.buyers.map((buyer, index) => (
            <div className="sig-box" key={index}>
              <p className="sig-label">BUYER {formData.buyers.length > 1 ? index + 1 : ''}:</p>
              <p>{buyer.representativeName || '___________________'}</p>
              <p>EID: {buyer.eid || '___________________'}</p>

              {/* Remote signature status badge */}
              {sigStatuses[index] && (
                <div className={`remote-sig-status ${sigStatuses[index] === 'completed' ? 'signed' : 'pending'}`}>
                  {sigStatuses[index] === 'completed' ? '✓ Signed Remotely' : '⏳ Awaiting Remote Signature'}
                </div>
              )}

              <p className="sig-label" style={{ marginTop: '10px' }}>Signature:</p>

              {/* Show remote signature image if available, else show canvas */}
              {remoteSignatures[index] ? (
                <div className="remote-sig-display">
                  <img src={remoteSignatures[index]} alt={`${buyer.representativeName} signature`} className="remote-sig-img" />
                </div>
              ) : (
                <>
                  <SignatureCanvas ref={el => buyerSigRef.current[index] = el} penColor="black" canvasProps={{ width: 220, height: 100, className: 'sig-canvas' }} />
                  <button id={`buyer-clear-btn-${index}`} className="btn-clear-sig hide-on-pdf" onClick={() => buyerSigRef.current[index].clear()}>Clear</button>
                </>
              )}

              {/* Remote photo if available */}
              {remotePhotos[index] ? (
                <div className="remote-photo-display hide-on-pdf">
                  <p className="sig-label" style={{ marginTop: '8px' }}>Verification Photo:</p>
                  <img src={remotePhotos[index]} alt={`${buyer.representativeName} verification`} className="remote-photo-img" />
                </div>
              ) : (
                <div className="camera-section hide-on-pdf">
                  {!cameraActive[index] && !buyerPhotos[index] && (
                    <button className="btn-take-photo" onClick={() => startCamera(index)}>📷 Take Photo</button>
                  )}
                  {cameraActive[index] && (
                    <div className="camera-preview">
                      <video id={`camera-video-${index}`} autoPlay playsInline className="camera-video" />
                      <div className="camera-actions">
                        <button className="btn-capture" onClick={() => capturePhoto(index, buyer.representativeName)}>⚪ Capture</button>
                        <button className="btn-cancel-camera" onClick={() => stopCamera(index)}>✕ Cancel</button>
                      </div>
                    </div>
                  )}
                  {buyerPhotos[index] && (
                    <div className="captured-photo">
                      <img src={buyerPhotos[index].dataUrl} alt={buyer.representativeName} />
                      <button className="btn-retake" onClick={() => { setBuyerPhotos(prev => { const u = { ...prev }; delete u[index]; return u; }); startCamera(index); }}>🔄 Retake</button>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
          {showGuarantor && (
            <div className="sig-box sig-box-guarantor">
              <p className="sig-label">GUARANTOR:</p>
              <p>{formData.guarantorName || '___________________'}</p>
              <p>CNIC/EID: {formData.guarantorEid || '___________________'}</p>
              <p className="sig-label" style={{ marginTop: '10px' }}>Signature:</p>
              <SignatureCanvas
                ref={guarantorSigRef}
                penColor="black"
                canvasProps={{ width: 220, height: 100, className: 'sig-canvas' }}
              />
              <button className="btn-clear-sig hide-on-pdf" onClick={() => guarantorSigRef.current?.clear()}>Clear</button>
            </div>
          )}
        </div>

        {formData.buyers.some(b => b.idFrontUrl || b.idBackUrl) && (
          <div className="preview-ids-page">
            <div className="preview-title"><h2>CUSTOMER IDENTIFICATION DOCUMENTS</h2></div>
            {formData.buyers.filter(b => b.idFrontUrl || b.idBackUrl).map((buyer, index) => (
              <div className="preview-id-card" key={index}>
                <p className="preview-id-name">{buyer.representativeName || `Buyer ${index + 1}`}</p>
                <div className="preview-id-sides">
                  <div className="preview-id-side"><span>FRONT</span>{buyer.idFrontUrl ? <img src={buyer.idFrontUrl} alt="ID Front" /> : <div className="preview-id-placeholder">Not uploaded</div>}</div>
                  <div className="preview-id-side"><span>BACK</span>{buyer.idBackUrl ? <img src={buyer.idBackUrl} alt="ID Back" /> : <div className="preview-id-placeholder">Not uploaded</div>}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        {Object.keys(buyerPhotos).length > 0 && (
          <div className="preview-ids-page">
            <div className="preview-title"><h2>BUYER PHOTOGRAPHS</h2></div>
            <div className="preview-ids-grid">
              {Object.entries(buyerPhotos).map(([index, photo]) => (
                <div className="preview-id-card" key={index}>
                  <p className="preview-id-name">{photo.buyerName || `Buyer ${parseInt(index) + 1}`}</p>
                  <img src={photo.dataUrl} alt={photo.buyerName} style={{ width: '100%', borderRadius: '6px' }} />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AgreementPreview;